import React, { useEffect, useState } from "react";
import WizardPageWrapper, { GeneralPageSummary } from "../WizardPageWrapper";
import { Button, Checkbox, Form, FormInstance, Typography } from "antd";
import Flex from "../../../../../../components/Flex";
import FormGroupItem from "../FormGroupItem";
import { CoverWizardForm, FormGroupTouched } from "../../CoverWizardModal";

enum CoverMaterial {
  MeycoLiteMesh = "MeycoLite Mesh",
  RuggedMesh = "Rugged-Mesh",
  PermaGuardSolidWithPump = "PermaGuard Solid With Pump",
  PermaGuardSolidWithSelfDraining = "PermaGuard Solid With Self Draining",
}
const coverMaterialCheckboxes = Object.values(CoverMaterial).map((value) => ({
  label: value,
  value,
}));
const coverMaterialFieldName = "coverMaterial";

enum CoverSpacing {
  ThreeByThree = "3x3 Spacing",
  FiveByFive = "5x5 Spacing",
  BestWay = "Best Way",
}
const coverSpacingCheckboxes = Object.values(CoverSpacing).map((value) => ({
  label: value,
  value,
}));
const coverSpacingFieldName = "coverSpacing";

enum CoverColor {
  Green = "Green",
  Blue = "Blue",
  Black = "Black",
  StoneGray = "Stone Gray",
  Mocha = "Mocha",
}
const coverColorCheckboxes = Object.values(CoverColor).map((value) => ({
  label: value,
  value,
}));
const coverColorFieldName = "coverColor";

type PoolOwnerInfoPageProps = {
  formIsSubmitting?: boolean;
  onBackButtonClick: () => void;
  onNextButtonClick: () => void;
  //the current form values are needed in order to have field values at mount (useWatch outputs current values at second rerender)
  initialFormValues: CoverWizardForm;
  formGroupTouchedState: [FormGroupTouched, React.Dispatch<FormGroupTouched>];
};

export default function MaterialSpacingColorPage({
  formIsSubmitting = false,
  onBackButtonClick,
  onNextButtonClick,
  initialFormValues,
  formGroupTouchedState: [touchedGroups, setTouchedGroups],
}: PoolOwnerInfoPageProps) {
  const form = Form.useFormInstance<CoverWizardForm>();

  const coverMaterial =
    Form.useWatch<string[]>(coverMaterialFieldName, { form, preserve: true }) ??
    initialFormValues.coverMaterial;

  const coverSpacing =
    Form.useWatch<string[]>(coverSpacingFieldName, { form, preserve: true }) ??
    initialFormValues.coverSpacing;

  const coverColor =
    Form.useWatch<string[]>(coverColorFieldName, { form, preserve: true }) ??
    initialFormValues.coverColor;

  return (
    <WizardPageWrapper
      nextIsLoading={formIsSubmitting}
      title="Material/Spacing/Color"
      summary={<GeneralPageSummary />}
      onBackButtonClick={onBackButtonClick}
      onNextButtonClick={onNextButtonClick}
    >
      <Typography.Paragraph italic style={{ marginBottom: "1.2rem" }}>
        Select the material, color, and spacing
      </Typography.Paragraph>
      <Flex vertical gap="16px">
        <FormGroupItem
          label="Material"
          showLabelOnExpanded
          closeButtonText={touchedGroups.materialGroup ? "Save" : "Next"}
          onGroupClosed={() =>
            setTouchedGroups({ ...touchedGroups, materialGroup: true })
          }
          notExpandedDisplayValue={
            !!coverMaterial.length ? coverMaterial.join(", ") : "None"
          }
          expandedOnMount={!touchedGroups.materialGroup}
        >
          <Flex vertical gap="0.4rem">
            <Form.Item<CoverWizardForm>
              style={{ margin: "0" }}
              name={coverMaterialFieldName}
            >
              <Checkbox.Group
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.5rem",
                }}
                options={coverMaterialCheckboxes}
              />
            </Form.Item>
          </Flex>
        </FormGroupItem>
        {touchedGroups.materialGroup && (
          <Form.Item<CoverWizardForm>
            style={{ margin: "0" }}
            name={coverSpacingFieldName}
          >
            <FormGroupItem
              label="Spacing"
              showLabelOnExpanded
              onGroupClosed={() =>
                setTouchedGroups({ ...touchedGroups, spacingGroup: true })
              }
              closeButtonText={touchedGroups.colorGroup ? "Save" : "Next"}
              notExpandedDisplayValue={
                !!coverSpacing.length ? coverSpacing.join(", ") : "None"
              }
              expandedOnMount={!touchedGroups.spacingGroup}
            >
              <Flex vertical gap="0.4rem">
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name="coverSpacing"
                >
                  <Checkbox.Group
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                    options={coverSpacingCheckboxes}
                  />
                </Form.Item>
                <Typography.Text>
                  *Some combinations of spacing and color may incur a 15%
                  upcharge
                </Typography.Text>
              </Flex>
            </FormGroupItem>
          </Form.Item>
        )}
        {touchedGroups.spacingGroup && (
          <Form.Item<CoverWizardForm>
            style={{ margin: "0" }}
            name={coverColorFieldName}
          >
            <FormGroupItem
              label="Color"
              showLabelOnExpanded
              notExpandedDisplayValue={
                !!coverColor.length ? coverColor.join(", ") : "None"
              }
              onGroupClosed={() =>
                setTouchedGroups({ ...touchedGroups, colorGroup: true })
              }
              expandedOnMount={!touchedGroups.colorGroup}
            >
              <Flex vertical gap="0.4rem">
                <Form.Item<CoverWizardForm>
                  style={{ margin: "0" }}
                  name="coverColor"
                >
                  <Checkbox.Group
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5rem",
                    }}
                    options={coverColorCheckboxes}
                  />
                </Form.Item>
                <Typography.Text>
                  *Some combinations of spacing and color may incur a 15%
                  upcharge
                </Typography.Text>
              </Flex>
            </FormGroupItem>
          </Form.Item>
        )}
      </Flex>
    </WizardPageWrapper>
  );
}
